import React, { useState } from 'react';
import { X } from 'lucide-react';
import { renderSafeHtml } from '../../../utils/renderHtml';
import { ExpandableListCell } from '../../ExpandableCell';
import {
  GRAY_200,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_700,
} from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';

/** One contributing source within a grouped name (e.g. "SAP AML", "GLEIF",
 * "FDA Orange Book"). `owners` is null for roles with no owner concept
 * (e.g. a manufacturer's own captured names) — never an empty array. */
export interface NamesTabSource {
  label: string;
  owners: string[] | null;
}

/** One distinct name, with every source that reported it. */
export interface NamesTabGroup {
  key: string;
  name: string;
  sources: NamesTabSource[];
}

export type NamesTabLoadState = 'idle' | 'loading' | 'success' | 'error';

interface NamesTabProps {
  /** Floating label text for the preferred-name input, e.g. "Product Name". */
  inputLabel: string;
  /** Content for the top instruction paragraph — callers compose their own
   * precedence-order tooltip into this (Product and Manufacturer use
   * different tooltip components). */
  hintText: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isDisabled: boolean;
  /** Shared modal style tokens (`floatingFieldContainer`, etc.) — the same
   * object passed to ModalFieldRenderer/useEditableModal. */
  styles: Record<string, React.CSSProperties>;
  getFloatingLabelStyle?: (
    fieldName: string,
    hasValue: boolean
  ) => React.CSSProperties;
  getFloatingInputStyle?: (
    isTextarea: boolean,
    fieldName: string
  ) => React.CSSProperties;
  /** Input `id`/`htmlFor` and the `getFloating*Style` field-name key.
   * Defaults are fine unless a modal renders two NamesTab instances at once. */
  fieldId?: string;

  variantsLabel: string;
  variantsHintText: string;
  groups: NamesTabGroup[];
  selectedGroupKey: string | null;
  /** Called with the clicked group, or null when the already-selected group
   * is clicked again (deselect-to-clear — mirrors a native radio group that
   * can be cleared). */
  onSelectGroup: (group: NamesTabGroup | null) => void;
  loadState?: NamesTabLoadState;
  loadErrorText?: string;
  loadingText?: string;
  emptyText?: string;

  /**
   * Async-write affordances. Product leaves these unset — its preferred
   * name is plain local state committed by the modal's Save button.
   * Manufacturer's preferred name lives in a separate table and is written
   * via its own API call as soon as a selection is made, so it needs a
   * pending/error surface independent of the modal's own save state.
   */
  pending?: boolean;
  bannerError?: string | null;
  /** e.g. "Currently displayed" — omitted entirely when not supplied. */
  selectedBadgeText?: string;
}

/**
 * Shared Names-tab UI (issue #653, Task 16 QA follow-up) — extracted from
 * ProductModal.tsx's `renderNamesTab` (previously two SynonymsRadioField
 * calls with `inputOnly`/`synonymsOnly`) so Product and Manufacturer render
 * an identical preferred-name input + grouped-variant list instead of two
 * independently-styled implementations. Layout: instruction hint,
 * floating-label input (with an X-to-clear button), a variants heading +
 * instruction, then the grouped radio list.
 */
const NamesTab: React.FC<NamesTabProps> = ({
  inputLabel,
  hintText,
  value,
  onChange,
  onFocus,
  onBlur,
  isDisabled,
  styles,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  fieldId = 'names-tab-preferred-name',
  variantsLabel,
  variantsHintText,
  groups,
  selectedGroupKey,
  onSelectGroup,
  loadState = 'success',
  loadErrorText = 'Failed to load name variants. Try closing and reopening the modal.',
  loadingText = 'Loading name variants...',
  emptyText = 'No name variants available',
  pending = false,
  bannerError = null,
  selectedBadgeText,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const shouldShowPlaceholder = isFocused && !value;

  const handleOptionClick = (group: NamesTabGroup) => {
    if (isDisabled || pending) return;
    onSelectGroup(selectedGroupKey === group.key ? null : group);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}>
      <p style={componentStyles.editHint}>{hintText}</p>

      <div style={styles.floatingFieldContainer}>
        <input
          id={fieldId}
          type="text"
          value={value}
          disabled={isDisabled}
          onChange={e => onChange(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          style={
            getFloatingInputStyle
              ? {
                  ...getFloatingInputStyle(false, fieldId),
                  paddingRight: '28px',
                }
              : {}
          }
          placeholder={
            shouldShowPlaceholder
              ? 'Type a name, or select from the variants below'
              : ' '
          }
        />
        <label
          htmlFor={fieldId}
          style={
            getFloatingLabelStyle ? getFloatingLabelStyle(fieldId, !!value) : {}
          }>
          {inputLabel}
        </label>
        {!isDisabled && value && (
          <button
            type="button"
            aria-label={`Clear ${inputLabel.toLowerCase()}`}
            // Keep focus on the input across this click (mousedown fires,
            // and would blur the input, before click does) — otherwise a
            // caller with blur-triggered commit logic (Manufacturer submits
            // the draft on blur) races: blur fires and submits the
            // still-there text first, then this click's onChange('') loses
            // to that submit's async state update. Product has no such
            // blur side effect, so it never showed this, but keeping focus
            // put is correct either way.
            onMouseDown={e => e.preventDefault()}
            onClick={() => onChange('')}
            style={componentStyles.clearButton}>
            <X size={14} />
          </button>
        )}
      </div>

      <div style={componentStyles.synonymsSection}>
        <div style={componentStyles.synonymsLabel}>{variantsLabel}</div>
        <p style={componentStyles.synonymsHelperText}>{variantsHintText}</p>

        {bannerError && (
          <div
            style={{ color: COLOR_DANGER, fontSize: '13px', padding: '4px 0' }}>
            {bannerError}
          </div>
        )}

        {loadState === 'error' ? (
          <div
            style={{ color: COLOR_DANGER, fontSize: '13px', padding: '8px 0' }}>
            {loadErrorText}
          </div>
        ) : loadState === 'idle' || loadState === 'loading' ? (
          <div style={{ color: GRAY_500, fontSize: '13px', padding: '8px 0' }}>
            {loadingText}
          </div>
        ) : groups.length === 0 ? (
          <div style={componentStyles.noSynonyms}>{emptyText}</div>
        ) : (
          <div style={componentStyles.synonymsContainer}>
            {groups.map(group => {
              const isSelected = selectedGroupKey === group.key;
              const isRowPending = pending && isSelected;
              return (
                <label
                  key={group.key}
                  style={{
                    ...componentStyles.synonymRow,
                    cursor: isDisabled ? 'default' : 'pointer',
                    opacity: isRowPending ? 0.6 : 1,
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                  }}>
                  <input
                    type="radio"
                    name={`${fieldId}-variant`}
                    checked={isSelected}
                    disabled={isDisabled || pending}
                    // onClick (not onChange): clicking an already-checked radio
                    // fires no native "change" event, but must still toggle it
                    // off (deselect-to-clear). onClick fires on every genuine
                    // click regardless of checked-state transition, and —
                    // because this is a real, focusable, non-disabled <input>
                    // — also fires on keyboard Space/Enter.
                    onClick={() => handleOptionClick(group)}
                    onChange={() => {}}
                    style={{
                      marginRight: '8px',
                      cursor: isDisabled ? 'default' : 'pointer',
                      alignSelf: 'flex-start',
                      marginTop: '2px',
                    }}
                  />
                  <div style={componentStyles.synonymOptionBody}>
                    <span style={componentStyles.synonymText}>
                      {renderSafeHtml(group.name)}
                      {isSelected && selectedBadgeText && (
                        <span style={componentStyles.selectedBadge}>
                          {selectedBadgeText}
                        </span>
                      )}
                    </span>
                    <div style={componentStyles.synonymSourcesList}>
                      {group.sources.map(source => (
                        <div
                          key={source.label}
                          style={componentStyles.synonymSourceRow}>
                          <span style={componentStyles.variantSourceLabel}>
                            {source.label}
                          </span>
                          {source.owners && source.owners.length > 0 && (
                            <div style={componentStyles.synonymOwnerList}>
                              <ExpandableListCell items={source.owners} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const componentStyles: Record<string, React.CSSProperties> = {
  editHint: {
    margin: '0 0 12px',
    fontSize: '13px',
    color: GRAY_500,
    lineHeight: '1.5',
  },
  clearButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    padding: '4px',
    margin: 0,
    cursor: 'pointer',
    color: GRAY_400,
    display: 'inline-flex',
    alignItems: 'center',
  },
  synonymsSection: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  synonymsLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: GRAY_700,
    marginBottom: '4px',
  },
  synonymsHelperText: {
    margin: '0 0 8px',
    fontSize: '12px',
    color: GRAY_500,
    lineHeight: '1.4',
  },
  noSynonyms: {
    fontSize: '13px',
    color: GRAY_400,
    fontStyle: 'italic',
    padding: '4px 0',
  },
  synonymsContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    padding: '8px',
    backgroundColor: GRAY_50,
  },
  synonymRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '6px 4px',
    borderRadius: '3px',
  },
  synonymOptionBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: '2px',
  },
  synonymText: {
    fontSize: '14px',
    color: GRAY_700,
    wordBreak: 'break-word',
  },
  selectedBadge: {
    marginLeft: '8px',
    fontSize: '11px',
    color: GRAY_500,
    fontWeight: 500,
  },
  synonymSourcesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  synonymSourceRow: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '4px',
  },
  synonymOwnerList: {
    display: 'inline-flex',
    fontSize: '11px',
    color: GRAY_500,
  },
  variantSourceLabel: {
    fontSize: '11px',
    color: GRAY_400,
    whiteSpace: 'nowrap',
  },
};

export default NamesTab;
